import React from 'react';
import { useHistory } from 'react-router';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import { DashboardPageHero } from 'containers/DashboardPage/DashboardPageHero';
import { DashboardPageSubtitle } from 'containers/DashboardPage/DashboardPageSubtitle';
import { DashboardPageButton } from 'containers/DashboardPage/DashboardPageButton';
import { DashboardPageWrapper } from 'containers/DashboardPage/DashboardPageWrapper';
import { DashboardPageBody } from 'containers/DashboardPage/DashboardPageBody';
import { DashboardTable } from 'containers/DashboardTable';
import { UtilityPageLayout } from 'design/utilityPageLayout';
import { TDashboardPageData } from 'types/state';
import { useServiceState } from 'hooks/useServiceState';
import { routes } from 'utils/routes';
import { setCustomInterval } from 'utils/setCustomInterval';
import { DASHBOARD_PAGE_DATA_UPDATE_INTERVAL } from 'utils/constants';
import { useUserRoles } from 'hooks/useUserRole';
import { PreloaderComponent } from 'design/preloader/preloaderComponents';

export const DashboardPage = () => {
  const history = useHistory();

  // Подюключает сервисы на получение данных страницы
  // и на завершение сессии.
  const { getDashboardPageData: getPageDataService } = useServices();

  // Подключает интернационализацию.
  const { logOutButton: signOutButtonText } = useIntlDictionary();

  // Подключает роли авторизованного пользователя.
  const userRoles = useUserRoles();

  // Локальное состояние для содержимого страницы.
  const [content, setContent] = React.useState<null | TDashboardPageData>(null);

  // Состояние и метод вызова сервиса на получение данных страницы.
  const [pageDataState, getPageData] = useServiceState(getPageDataService);

  // Эффект вызывает сервис на получение данных страницы
  // после монтирования (getPageData и userRole, по задумке
  // не должны меняться).
  React.useEffect(() => {
    getPageData({ userRoles });
  }, [getPageData, userRoles]);

  // Эффект следит за состоянием сервиса на получение данных
  // и в случае успеха записывает данные страницы в состояние.
  React.useEffect(() => {
    if (pageDataState && pageDataState.resolvedWith) {
      setContent(pageDataState.resolvedWith);
    }
  }, [pageDataState]);

  React.useEffect(() => {
    return setCustomInterval(() => {
      getPageData({ userRoles });
    }, DASHBOARD_PAGE_DATA_UPDATE_INTERVAL);
  }, [getPageData, pageDataState, userRoles]);

  // Обработчик намерения завершить сессию.
  // Вызывает сервис на завершение сессии.
  const onSignOut = React.useCallback(() => {
    history.push(routes.signOut());
  }, [history]);

  // Обработчик успешно завершенного редактирования данных страницы.
  // Вызывает сервис на получение актуальных данных страницы.
  const onContentEdited = React.useCallback(() => {
    getPageData({ userRoles });
  }, [getPageData, userRoles]);

  const subtitle = content ? content.accountInfo.email : '';

  return (
    <UtilityPageLayout width="xl" headerNode={null} footerNode={null}>
      {content ? (
        <DashboardPageWrapper>
          <DashboardPageHero
            titleNode={null}
            subtitleNode={<DashboardPageSubtitle text={subtitle} />}
            actionNode={
              <DashboardPageButton
                text={signOutButtonText}
                onClick={onSignOut}
              />
            }
          />
          <DashboardPageBody>
            <DashboardTable
              content={content}
              onContentEdited={onContentEdited}
            />
          </DashboardPageBody>
        </DashboardPageWrapper>
      ) : (
        <PreloaderComponent />
      )}
    </UtilityPageLayout>
  );
};
